import { Select, Spin, Table, Button, Space } from 'antd';
import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { prepareTable } from './utils/prepareDynamicTable.ts';
import { useMsal } from '@azure/msal-react';
import { getAssetsByDiscipline } from './services/discipline.service.ts';
import { DisciplineEnum } from './enums/disciplineEnum.ts';
import { useNavigate, useParams } from 'react-router-dom';

const Disciplines: React.FC = () => {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const navigate = useNavigate();
  const { plantId } = useParams<{ plantId: string }>();

  const [assets, setAssets] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [visibleCols, setVisibleCols] = useState<string[]>([]);
  const [currentDiscipline, setCurrentDiscipline] = useState<DisciplineEnum>(
    DisciplineEnum.MECHANICAL,
  );

  const tr = {
    discipline: t('discipline'),
    mechanical: t('mechanical'),
    electrical: t('electrical'),
    plumbing: t('plumbing'),
    instrumentation: t('instrumentation'),
    select_discipline: t('select_discipline'),
    select_columns: t('select_columns'),
  };

  const { columns, dataSource } = useMemo(() => prepareTable(assets, t), [assets, t]);

  const filteredColumns = useMemo(
    () => (visibleCols.length === 0 ? columns : columns.filter((c) => visibleCols.includes(c.key))),
    [columns, visibleCols],
  );

  const getAllAssets = async (
    discipline: DisciplineEnum,
    currentPage: number = 1,
    currentPageSize: number = pageSize,
  ) => {
    setLoading(true);
    try {
      const res = await getAssetsByDiscipline(instance, {
        discipline,
        location: plantId || '',
        page: currentPage,
        limit: currentPageSize,
      });

      // Handle different response formats
      if (Array.isArray(res)) {
        // If response is an array, use client-side pagination
        setAssets(res);
        setTotalCount(res.length);
      } else {
        // If response is an object with items
        setAssets(res.items || []);
        // Try different possible field names for total count
        const total =
          res.totalItems || res.totalCount || res.total || (res.items ? res.items.length : 0);
        setTotalCount(total);
      }

      setPage(currentPage);
      setPageSize(currentPageSize);
      setCurrentDiscipline(discipline);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initialize with mechanical discipline
    getAllAssets(DisciplineEnum.MECHANICAL, 1, pageSize);
  }, []);

  return (
    <div className='interconnectionsContent'>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space>
          <Button
            type={currentDiscipline === DisciplineEnum.MECHANICAL ? 'primary' : 'default'}
            disabled={loading}
            onClick={() => getAllAssets(DisciplineEnum.MECHANICAL, 1, pageSize)}
          >
            {tr.mechanical}
          </Button>
          <Button
            type={currentDiscipline === DisciplineEnum.ELECTRICAL ? 'primary' : 'default'}
            disabled={loading}
            onClick={() => getAllAssets(DisciplineEnum.ELECTRICAL, 1, pageSize)}
          >
            {tr.electrical}
          </Button>
          <Button
            type={currentDiscipline === DisciplineEnum.PLUMBING ? 'primary' : 'default'}
            disabled={loading}
            onClick={() => getAllAssets(DisciplineEnum.PLUMBING, 1, pageSize)}
          >
            {tr.plumbing}
          </Button>
          <Button
            type={currentDiscipline === DisciplineEnum.INSTRUMENTATION ? 'primary' : 'default'}
            disabled={loading}
            onClick={() => getAllAssets(DisciplineEnum.INSTRUMENTATION, 1, pageSize)}
          >
            {tr.instrumentation}
          </Button>
        </Space>
        
        <Select
          mode='multiple'
          allowClear
          style={{ width: 400 }}
          placeholder={tr.select_columns}
          value={visibleCols}
          onChange={(vals) => setVisibleCols(vals)}
          optionLabelProp='label'
        >
          {columns.map((c) => (
            <Select.Option key={c.key} value={c.key} label={c.title}>
              {c.title}
            </Select.Option>
          ))}
        </Select>
      </div>

      {loading ? (
        <Spin />
      ) : (
        <Table
          columns={filteredColumns}
          dataSource={dataSource}
          rowKey='id'
          tableLayout='auto'
          scroll={{ x: columns.length * 160, y: 600 }}
          pagination={{
            current: page,
            pageSize,
            total: totalCount,
            onChange: (p, ps) => {
              getAllAssets(currentDiscipline, p, ps || pageSize);
            },
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} itens`,
          }}
          onRow={(record) => ({
            onClick: () => navigate(`/maintenance/plants/${plantId}/assets/${record.id}`),
            style: { cursor: 'pointer' },
          })}
        />
      )}
    </div>
  );
};

export default Disciplines;
